/**
 * Keeps the game's one Web Audio context alive across phone calls, Siri,
 * alarms and other apps' audio. Ported from Single Didge's audio-keeper.js
 * (same behavior, now typed); shared by the sound effects and Tibbs.
 *
 * What the phone does to a page's audio:
 *   iOS     a call, Siri, an alarm, or music started in another app moves the
 *           AudioContext to the non-standard 'interrupted' state (or
 *           'suspended'). When the interruption ends the context often does
 *           NOT come back by itself, and after some calls resume() never
 *           succeeds again (or the context reports 'running' with a frozen clock).
 *   Android audio focus loss can suspend a context the same way.
 *
 * What this does about it:
 *   - Every state change, every return to the page, and every tap or key press
 *     asks the context to resume when it is not running and the page is visible.
 *   - A resume refused outside a gesture is retried on the next tap.
 *   - A context that is closed, that a tap already failed to resume, or whose
 *     clock is frozen while it claims to be running, is replaced by a fresh one
 *     INSIDE the next tap (where iOS allows a new context to start).
 *     `onReplace(old, fresh)` lets the owner rebuild anything it cached.
 *
 * Everything is injectable so the logic can be unit-tested with a fake context.
 */

export type KeeperState = AudioContextState | 'interrupted' | 'stalled';

/** The slice of AudioContext the keeper uses (so tests can pass a fake). */
export interface KeptContext {
  readonly state: AudioContextState | 'interrupted';
  readonly currentTime: number;
  readonly sampleRate: number;
  readonly destination: AudioNode;
  resume(): Promise<void>;
  close(): Promise<void>;
  createBuffer(channels: number, length: number, rate: number): AudioBuffer;
  createBufferSource(): AudioBufferSourceNode;
  addEventListener(type: 'statechange', fn: () => void): void;
}

interface EventTargetLike {
  addEventListener(type: string, fn: (e: Event) => void, opts?: AddEventListenerOptions | boolean): void;
  removeEventListener(type: string, fn: (e: Event) => void, opts?: EventListenerOptions | boolean): void;
}

interface DocLike extends EventTargetLike {
  visibilityState?: DocumentVisibilityState;
}

export interface KeeperOptions<C extends KeptContext> {
  create: () => C;
  doc?: DocLike | null;
  win?: EventTargetLike | null;
  onReplace?: (old: C, fresh: C) => void;
  onState?: (state: KeeperState, ctx: C) => void;
  onWake?: (info: { gesture: boolean; reason: string }) => void;
  log?: (event: string, extra?: Record<string, unknown>) => void;
  resumeTimeoutMs?: number;
  probeMs?: number;
  tapGapMs?: number;
  now?: () => number;
}

export interface AudioKeeper<C extends KeptContext> {
  /** The live context, created (and a resume requested) as needed. Always use this, never a saved copy. */
  get(): C;
  /** The current context without creating one. */
  current(): C | null;
  resume(): Promise<boolean>;
  install(): void;
  uninstall(): void;
  readonly strikes: number;
  readonly stalled: boolean;
}

const GESTURES = ['pointerdown', 'touchend', 'keydown', 'click'] as const;

export function createAudioKeeper<C extends KeptContext>(opts: KeeperOptions<C>): AudioKeeper<C> {
  const {
    create,
    onReplace = () => {},
    onState = () => {},
    onWake = () => {},
    log = () => {},
    resumeTimeoutMs = 1500,
    probeMs = 800,
    tapGapMs = 1000,
    now = () => Date.now(),
  } = opts;
  let ctx: C | null = null;
  let strikes = 0;
  let tap = 0;
  let lastGestureAt = -Infinity;
  let struckTap = -1;
  let stalled = false;
  let pending: Promise<boolean> | null = null;
  let probing: ReturnType<typeof setTimeout> | null = null;
  let installed = false;
  const D = (): DocLike | null => (opts.doc !== undefined ? opts.doc : typeof document !== 'undefined' ? document : null);
  const W = (): EventTargetLike | null => (opts.win !== undefined ? opts.win : typeof window !== 'undefined' ? window : null);
  const visible = () => {
    const d = D();
    return !d || d.visibilityState !== 'hidden';
  };
  const later = (fn: () => void, ms: number) => setTimeout(fn, ms);

  function make(): C {
    const old = ctx;
    const fresh = create();
    ctx = fresh;
    strikes = 0;
    struckTap = -1;
    stalled = false;
    pending = null;
    try {
      fresh.addEventListener('statechange', () => {
        if (fresh === ctx) changed();
      });
    } catch {
      /* ignore */
    }
    if (old) {
      log('replace', { from: old.state });
      try {
        if (old.state !== 'closed') old.close()?.catch?.(() => {});
      } catch {
        /* ignore */
      }
      try {
        onReplace(old, fresh);
      } catch {
        /* ignore */
      }
    }
    install();
    return fresh;
  }

  function changed(): void {
    const c = ctx!;
    const s = c.state;
    log('state', { state: s });
    if (s === 'running') strikes = 0;
    try {
      onState(s, c);
    } catch {
      /* ignore */
    }
    if (s !== 'running' && s !== 'closed') void resume();
  }

  function resume(): Promise<boolean> {
    const a = ctx;
    if (!a || a.state === 'closed') return Promise.resolve(false);
    if (a.state === 'running') return Promise.resolve(true);
    if (!visible()) return Promise.resolve(false);
    if (pending) return pending;
    const p = new Promise<boolean>((done) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        if (pending === p) pending = null;
        done(a === ctx && a.state === 'running');
      };
      try {
        Promise.resolve(a.resume()).then(finish, finish);
      } catch {
        finish();
      }
      later(finish, resumeTimeoutMs);
    });
    pending = p;
    return p;
  }

  function probe(a: C | null, again = false): void {
    if ((probing && !again) || !a || a.state !== 'running' || !visible()) return;
    const t = a.currentTime;
    probing = later(() => {
      probing = null;
      if (a !== ctx || a.state !== 'running' || !visible() || stalled) return;
      if (a.currentTime !== t) return;
      if (!again) return probe(a, true);
      stalled = true;
      log('stalled', { at: t });
      try {
        onState('stalled', a);
      } catch {
        /* ignore */
      }
    }, probeMs);
  }

  // A one-sample silent buffer started inside the gesture is what finally unlocks iOS.
  function tick(a: C): void {
    try {
      const src = a.createBufferSource();
      src.buffer = a.createBuffer(1, 1, a.sampleRate);
      src.connect(a.destination);
      src.start(0);
    } catch {
      /* ignore */
    }
  }

  function onGesture(e: Event): void {
    if (!visible()) return;
    const t = now();
    const type = e?.type;
    if (type === 'pointerdown' || type === 'keydown' || t - lastGestureAt > tapGapMs) tap++;
    lastGestureAt = t;
    const thisTap = tap;
    try {
      onWake({ gesture: true, reason: type ?? 'gesture' });
    } catch {
      /* ignore */
    }
    if (ctx && ctx.state === 'running' && !stalled) return;
    let a = ctx;
    if (!a || a.state === 'closed' || stalled || (strikes > 0 && struckTap !== thisTap)) a = make();
    let p: Promise<void> | undefined;
    try {
      p = a.resume();
    } catch {
      /* ignore */
    }
    tick(a);
    let settled = false;
    const target = a;
    const check = () => {
      if (settled) return;
      settled = true;
      if (target !== ctx) return;
      if (target.state === 'running') {
        strikes = 0;
        probe(target);
      } else if (struckTap !== thisTap) {
        strikes++;
        struckTap = thisTap;
        log('resume-failed', { state: target.state, strikes });
      }
    };
    Promise.resolve(p).then(check, check);
    later(check, resumeTimeoutMs);
  }

  function onReturn(e: Event): void {
    if (!visible()) return;
    const a = ctx;
    if (a && a.state !== 'closed') {
      if (a.state === 'running') probe(a);
      else
        void resume().then((ok) => {
          if (ok) probe(a);
        });
    }
    try {
      onWake({ gesture: false, reason: e?.type ?? 'return' });
    } catch {
      /* ignore */
    }
  }

  const gestureOpts: AddEventListenerOptions = { capture: true, passive: true };

  function install(): void {
    if (installed) return;
    const d = D();
    if (!d?.addEventListener) return;
    installed = true;
    for (const g of GESTURES) d.addEventListener(g, onGesture, gestureOpts);
    d.addEventListener('visibilitychange', onReturn);
    const w = W();
    try {
      w?.addEventListener?.('pageshow', onReturn);
      w?.addEventListener?.('focus', onReturn);
    } catch {
      /* ignore */
    }
  }

  function uninstall(): void {
    if (!installed) return;
    installed = false;
    const d = D();
    for (const g of GESTURES) d?.removeEventListener?.(g, onGesture, { capture: true });
    d?.removeEventListener?.('visibilitychange', onReturn);
    const w = W();
    try {
      w?.removeEventListener?.('pageshow', onReturn);
      w?.removeEventListener?.('focus', onReturn);
    } catch {
      /* ignore */
    }
  }

  return {
    get() {
      if (!ctx || ctx.state === 'closed') make();
      if (ctx!.state !== 'running') void resume();
      return ctx!;
    },
    current: () => ctx,
    resume,
    install,
    uninstall,
    get strikes() {
      return strikes;
    },
    get stalled() {
      return stalled;
    },
  };
}

// ---------------------------------------------------------------------------
// iOS audio session (Single Didge's lesson)
// ---------------------------------------------------------------------------

interface SessionLike {
  type: string;
}
type NavLike = { audioSession?: SessionLike };

let sessionHolds = 0;
let sessionPrev: string | null = null;

/**
 * "ambient" up front makes the sound effects mix with other apps' audio and
 * keeps the ring/silent switch in charge. Call once at startup.
 */
export function declareAmbientSession(nav: NavLike | null = typeof navigator !== 'undefined' ? (navigator as unknown as NavLike) : null): boolean {
  let s: SessionLike | undefined;
  try {
    s = nav?.audioSession;
  } catch {
    /* ignore */
  }
  if (!s) return false;
  try {
    if (sessionHolds === 0 && s.type !== 'playback') {
      s.type = 'ambient';
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Hold the page's audio session at "playback" while Tibbs talks, so the
 * silent switch does not mute him; returns a release (idempotent; the last
 * release restores the previous type).
 */
export function holdPlaybackSession(nav: NavLike | null = typeof navigator !== 'undefined' ? (navigator as unknown as NavLike) : null): () => void {
  let s: SessionLike | undefined;
  try {
    s = nav?.audioSession;
  } catch {
    /* ignore */
  }
  if (!s) return () => {};
  const session = s;
  try {
    if (sessionHolds++ === 0) {
      sessionPrev = session.type;
      if (session.type !== 'playback') session.type = 'playback';
    }
  } catch {
    /* ignore */
  }
  let held = true;
  return () => {
    if (!held) return;
    held = false;
    try {
      if (--sessionHolds === 0 && sessionPrev && sessionPrev !== 'playback' && session.type === 'playback') session.type = sessionPrev;
    } catch {
      /* ignore */
    }
  };
}

/** Test hook. */
export function resetSessionHolds(): void {
  sessionHolds = 0;
  sessionPrev = null;
}
